import { useState, useEffect } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, useCollectionBinding } from '@vibesites/runtime/react';
import { RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// Property override of the Milburn PropertyOverview: the slideshow pulls from
// the full photo gallery (no FEATURED description filter). Inherited Milburn
// deviations from the Homestead PropertyOverview:
//   1. The "Explore Floor Plans" CTA matches the theme's other calls-to-action:
//      solid black with white text, flipping to a white background with black
//      text on hover (see the CTA anchor className below).
//   2. A tablet-only rebalance of the two-column row (see the 768–1023px media
//      query at the bottom of the style block).
// Everything else mirrors the parent — keep in sync with parent structural
// changes.

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Explore Floor Plans CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Label', type: 'text', defaultValue: 'Explore Floor Plans' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

interface SlideshowImage {
  image: string;
  alt_text?: string;
  title?: string;
  original_url?: string;
}

/**
 * Auto-advancing image slideshow for the right half of the overview section.
 * Photos come from the property `photo_gallery` collection via a collection
 * binding, so editor reorder/hide/inject overrides apply. Falls back to
 * `original_url` for items that lack a CDN `image`.
 */
function OverviewSlideshow({ images }: { images: SlideshowImage[] }) {
  const [current, setCurrent] = useState(0);
  const count = images.length;

  const safeCurrent = count > 0 ? Math.min(current, count - 1) : 0;

  // Auto-advance every 5s; pauses while the tab is hidden.
  useEffect(() => {
    if (count <= 1) return;
    const id = window.setInterval(() => {
      setCurrent((p) => (p + 1) % count);
    }, 5000);
    return () => window.clearInterval(id);
  }, [count]);

  if (count === 0) {
    return (
      <div className="overview-slideshow overview-slideshow--empty">
        <p className="font-body text-sm text-[var(--vibe-color-text-muted)]">
          Gallery images will be displayed here.
        </p>
      </div>
    );
  }

  return (
    <div className="overview-slideshow">
      {images.map((img, i) => (
        <div
          key={img.image || img.original_url || i}
          className={`overview-slideshow__slide${i === safeCurrent ? ' is-active' : ''}`}
          aria-hidden={i === safeCurrent ? undefined : true}
        >
          <VibesitesImage
            url={img.image || img.original_url || ''}
            alt={img.alt_text || img.title || ''}
            className="w-full h-full object-cover"
            sizes="(min-width: 768px) 400px, 100vw"
            priority={i === 0}
          />
        </div>
      ))}
    </div>
  );
}

export function PropertyOverview() {
  const ctaLink = useSlotGroup('propertyoverview.cta_link', ctaLinkDef);

  const slideshowImages = useCollectionBinding<SlideshowImage>('propertyoverview.slideshow', {
    collection: 'photo_gallery',
    defaultLimit: 8,
  });

  useRevealStyles();
  const headingRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="overview-section pt-12 md:pt-20 pb-12 md:pb-20 bg-[var(--vibe-color-background)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Mobile: slideshow above title+blurb. Desktop: text left, slideshow right. */}
        <div className="overview-grid grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
          <div
            ref={headingRef}
            className="overview-text reveal-fade-up order-2 md:order-1 text-center md:text-left md:pl-8 lg:pl-12"
          >
            <RichTextContent
              path="propertyoverview.title"
              as="h1"
              className="text-3xl md:text-4xl font-heading overview-title mb-6 leading-tight"
            />

            <RichTextContent
              path="propertyoverview.description"
              as="div"
              className="overview-blurb font-body text-[0.875rem] sm:text-[1rem] tracking-normal text-[var(--vibe-color-text)] max-w-3xl mx-auto md:mx-0 [&_a]:text-primary [&_a]:underline"
              style={{ lineHeight: 2 }}
            />

            <EditableSlotGroup groupPath="propertyoverview.cta_link" definition={ctaLinkDef}>
              <a
                href={ctaLink.url || '/floor-plans'}
                target={ctaLink._target || '_self'}
                className="overview-cta block w-full md:inline-block md:w-auto text-center mt-8 px-10 py-5 text-sm border border-black bg-black text-white hover:bg-white hover:text-black transition-all"
              >
                {ctaLink.label || 'Explore Floor Plans'}
              </a>
            </EditableSlotGroup>
          </div>

          <div className="overview-media reveal-fade-up order-1 md:order-2 md:pr-8 lg:pr-12">
            <OverviewSlideshow images={slideshowImages} />
          </div>
        </div>
      </div>

      <style>{`
        .overview-title {
          /* Page H1 (font-heading on the element). Color only here. */
          color: var(--vibe-color-text);
        }
        /* Sans body face + airy leading for page blurbs. !important beats
           Tailwind text-* line-heights and any heading inheritance. */
        .overview-blurb,
        .overview-blurb p,
        .overview-blurb span {
          font-family: var(--vibe-font-body) !important;
          letter-spacing: normal !important;
          line-height: 2 !important;
        }
        /* Match header / Community Info CTAs: body face, normal weight, tight tracking. */
        .overview-cta {
          font-family: var(--vibe-font-body) !important;
          font-weight: 400;
          letter-spacing: 0.03em;
        }
        .overview-slideshow {
          position: relative;
          width: 100%;
          max-width: 400px;
          margin-left: auto;
          margin-right: auto;
          aspect-ratio: 1;
          overflow: hidden;
          background: #000;
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12);
        }
        .overview-slideshow--empty {
          display: flex;
          align-items: center;
          justify-content: center;
          background: color-mix(in srgb, var(--vibe-color-primary) 8%, white);
          text-align: center;
          padding: 1.5rem;
        }

        .overview-slideshow__slide {
          position: absolute;
          inset: 0;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.6s ease;
        }
        .overview-slideshow__slide.is-active {
          opacity: 1;
          pointer-events: auto;
          z-index: 1;
        }

        @media (prefers-reduced-motion: reduce) {
          .overview-slideshow__slide { transition: none; }
        }

        /* Tablet: the two columns are at their narrowest here, and the desktop
           gutter plus the inset padding spend ~15% of the row on empty space.
           Reclaiming it widens the blurb's measure and lets the square photo
           grow, so the column heights land close enough to read as a pair.
           Descendant selectors (not !important) to outrank the md: utilities. */
        @media (min-width: 768px) and (max-width: 1023px) {
          .overview-section .overview-grid { gap: 2rem; }
          .overview-section .overview-text { padding-left: 0; }
          .overview-section .overview-media { padding-right: 0; }
          /* Airy desktop leading reads as slack at a short measure. */
          .overview-section .overview-blurb,
          .overview-section .overview-blurb p,
          .overview-section .overview-blurb span {
            line-height: 1.8 !important;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyOverview',
  displayName: 'Property Overview',
  tokens: {
    'propertyoverview.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<span data-type="field-token" data-field="property_name"></span>',
    },
    'propertyoverview.description': {
      label: 'Description',
      type: 'richtext',
      defaultField: 'property_long_description',
      defaultValue: '<p>Discover the perfect blend of comfort, style, and convenience at our community.</p>',
    },
  },
  slotGroups: {
    'propertyoverview.cta_link': ctaLinkDef,
  },
  collectionBindings: {
    'propertyoverview.slideshow': {
      label: 'Slideshow Photos',
      collection: 'photo_gallery',
      defaultLimit: 8,
      description: 'Overview slideshow images from the property photo gallery.',
    },
  },
};

(PropertyOverview as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default PropertyOverview;
